import React, { useState, useMemo, useEffect } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import moment from "moment";
import { calendarDateInputFormat } from "../../../utils/format";
import { mapBufferSettingsGroupsToOptions } from "../../../utils/mappers/calendar";
import { superGroupExists } from "../../../utils/mappers/groupSettings";
import { mapColumnsToGroupFields } from "../../../utils/mappers/genericGroups";
import SubgroupDropdown from "../../shared/MaterialCheckboxSelector";

import {
  Modal,
  Form,
  Header,
  Icon,
  Button,
  Confirm,
  Dropdown,
  Radio,
  Grid,
  Message,
  Segment,
  Dimmer,
  Loader,
} from "semantic-ui-react";

import { useTranslation } from "react-i18next";

import "./EventModal.css";

export const EventModal = ({ groups, event, onSubmit, onClose, onDelete, showGroupWarning, warningShowed, columns, items }) => {
  const { t } = useTranslation();

  const isEditing = _.get(event, "id", null) !== null;

  const [start, setStart] = useState(calendarDateInputFormat(_.get(event, 'start', null)));
  const [end, setEnd] = useState(calendarDateInputFormat(_.get(event, 'end', null)));
  const [title, setTitle] = useState(_.get(event, "title", ""));
  const [showRemoveEventConfirm, setShowRemoveEventConfirm] = useState(false);
  //const [createDemand, setCreateDemand] = useState(false);
  const [disableQtyPerDay, setDisableQtyPerDay] = useState(
    isEditing && _.get(event, "extendedProps.quantity_per_day", null) === null
  );
  const [qtyPerDay, setQtyPerDay] = useState(
    _.get(event, "extendedProps.quantity_per_day", null)
  );
  const [adusPerDay, setAdusPerDay] = useState(
    _.get(event, "extendedProps.adus_per_day", null)
  );
  const [selectedGroups, setSelectedGroups] = useState(
    mapBufferSettingsGroupsToOptions(
      _.get(event, "extendedProps.groups", []).map((currentEventGroup) => {
        const isRulesGroup = Object.keys(currentEventGroup).includes('rules');
        return (
          groups.find(group =>
            group.id === currentEventGroup.id &&
            Object.keys(group).includes('rules') === isRulesGroup
          ) || groups.find(group => group.id === currentEventGroup.id)
        );
      }).filter(Boolean)
    ).map((group) => group.value)
  );
  const [selectedMaterials, setSelectedMaterials] = useState(
    _.get(event, "extendedProps.materials") || []
  );
  const [selectedGroupError, setSelectedGroupError] = useState(false);
  const [endDateError, setEndDateError] = useState(null);
  const [isEventGroup, setIsEventGroup] = useState(true && selectedMaterials.length === 0);
  const groupFields = useMemo(() => mapColumnsToGroupFields(t, columns, items), [t, columns, items]);
  const granularMaterialInfo = useMemo(
    () => groupFields.find(field => field.accessor === 'location_code_concatenation'),
    [groupFields]
  );
  const [materialSelectorReady, setMaterialSelectorReady] = useState(() => !!granularMaterialInfo);
  useEffect(() => {
    if (granularMaterialInfo && !materialSelectorReady) {
      const raf = requestAnimationFrame(() => setMaterialSelectorReady(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [granularMaterialInfo]); // eslint-disable-line react-hooks/exhaustive-deps
  const groupOptions = useMemo(
    () => _.isEmpty(groups)
      ? []
      : mapBufferSettingsGroupsToOptions(
          groups.filter(group => Object.keys(group).includes('rules')
            ? !group.rules.materialMethod.includes('rules')
            : true)
        ),
    [groups]
  );

  useEffect(() => {
    if (superGroupExists(selectedGroups, groups) && !warningShowed) {
      showGroupWarning();
    }
  }, [selectedGroups, groups, warningShowed, showGroupWarning]);

  const onSave = (e) => {
    if (_.isEmpty(selectedGroups) && isEventGroup) {
      setSelectedGroupError(true);
      return;
    }

    // to-do: "end date value is exclusive?"
    e.preventDefault();

    const startDate = moment(start, "YYYY-MM-DD").format();

    let endDate;

    if (_.isEmpty(end)) {
      endDate = moment(start, "YYYY-MM-DD")
        .add(1, "days")
        .format();
    } else {
      endDate = moment(end, "YYYY-MM-DD").format();
    }

    if (moment(endDate).isBefore(startDate)) {
      setEndDateError({
        content: t("calendar.events.endDateBeforeStartError"),
        pointing: "above",
      });
      return;
    }

    if (moment(endDate).isSame(startDate)) {
      setEndDateError({
        content: t("calendar.events.eventDurationError"),
        pointing: "above",
      });
      return;
    }

    if (disableQtyPerDay) {
      onSubmit(title, startDate, endDate, null, adusPerDay, isEventGroup ? selectedGroups : [], isEventGroup ? [] : selectedMaterials);
    } else {

      onSubmit(title, startDate, endDate, qtyPerDay, null, isEventGroup ? selectedGroups : [], isEventGroup ? [] : selectedMaterials);
    }

    onClose();
  };
  return (
    <>
      <Confirm
        open={showRemoveEventConfirm}
        content={t("calendar.events.deleteConfirmMessage", {
          event: event.title,
        })}
        cancelButton={t("cancel")}
        confirmButton={t("confirm")}
        onCancel={() => setShowRemoveEventConfirm(false)}
        onClose={() => setShowRemoveEventConfirm(false)}
        onConfirm={() => {
          onDelete(event.id);
          onClose();
        }}
      />
      <Form onSubmit={onSave}>
        <Modal
          as={Form}
          className="event-modal"
          open
          size="small"
          onClose={onClose}
        >
          <Header
            icon="browser"
            content={
              isEditing ? t("calendar.events.edit") : t("calendar.events.add")
            }
          />
          <Modal.Content>
            <Form.Field required>
              <label>{t("calendar.events.titleLabel")}</label>
              <input
                className="event-title"
                placeholder={t("calendar.events.titleLabel")}
                name="title"
                required
                defaultValue={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Form.Field>

            <Form.Field required>
              <label>{t("calendar.events.eventMaterial")}</label>
              <Grid.Row className="event-material-section">
                <Button.Group fluid>
                    <Button 
                        type="button"
                        toggle 
                        active={isEventGroup}
                        onClick={() => setIsEventGroup(true)}
                        // disabled={disableAnalyticsContentInteraction}
                        >
                        {t("calendar.events.eventMaterialGroup")}
                    </Button>
                    <Button 
                        type="button"
                        toggle 
                        active={!isEventGroup}
                        onClick={() => setIsEventGroup(false)}
                        // disabled={disableAnalyticsContentInteraction}
                        >
                        {t("calendar.events.eventMaterialList")}
                    </Button>
                </Button.Group>
              </Grid.Row>
              {
                isEventGroup ? 
                  <Dropdown
                    required
                    error={selectedGroupError}
                    className="event-group-select"
                    placeholder={t("calendar.events.eventMaterialPlaceholder")}
                    fluid
                    selection
                    labeled
                    multiple
                    value={selectedGroups}
                    options={groupOptions}
                    onChange={(e, data) => {
                      setSelectedGroups(data.value);
                      setSelectedGroupError(false);
                    }}
                  />
                :
                  !materialSelectorReady ? (
                    <Segment className="material-loader-container">
                      <Dimmer active inverted>
                        <Loader size="tiny" />
                      </Dimmer>
                    </Segment>
                  ) : _.isEmpty(granularMaterialInfo.options) ? (
                    <Message className="no-material-container" info size="small" content={t("calendar.events.noMaterialsAvailable")} />
                  ) : (
                    <SubgroupDropdown
                      options={granularMaterialInfo.options}
                      defaultValues={selectedMaterials}
                      onChange={setSelectedMaterials}
                      defaultAlign={false}
                    />
                  )
                }
            </Form.Field>

            {/* <CustomCalendarInput
              className="event-start"
              title={t("calendar.events.startDateLabel")}
              required={true}
              onDateClick={(selectedDate) => setStart(selectedDate)}
              /> */}
            {/* TODO: add an icon to delete info easy, beacuase in this from doesn't need end date */}
            {/* <CustomCalendarInput
              className="event-end"
              title={t("calendar.events.endDateLabel")}
              error={endDateError}
              required={true}
              onDateClick={(selectedDate) => setEnd(selectedDate)}
              />  */}
            {/* TODO: Implement some features, like when user doesn't insert end date */}
            <Form.Field required>
              <label>{t("calendar.events.startDateLabel")}</label>
              <input
                className="event-start"
                type="date"
                name="start"
                required
                defaultValue={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </Form.Field>
            <Form.Input
              error={endDateError}
              label={t("calendar.events.endDateLabel")}
            >
              <input
                className="event-end"
                type="date"
                name="end"
                defaultValue={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </Form.Input>

            <Grid columns={2}>
              <Grid.Column>
                <Form.Field>
                  <Radio
                    className="per-day-radio"
                    label={t("calendar.events.quantityPerDay")}
                    name="per-day-radio-group"
                    checked={!disableQtyPerDay}
                    onChange={() => setDisableQtyPerDay(false)}
                  />
                </Form.Field>
                <Form.Field
                  required={!disableQtyPerDay}
                  disabled={disableQtyPerDay}
                >
                  <input
                    type="number"
                    step="any"
                    min="0"
                    defaultValue={_.get(
                      event,
                      "extendedProps.quantity_per_day",
                      null
                    )}
                    className="per-day-input"
                    name="adusPerDay"
                    required={!disableQtyPerDay}
                    onChange={(e) => setQtyPerDay(e.target.value)}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <Radio
                    className="per-day-radio"
                    label={t("calendar.events.adusPerDay")}
                    name="per-day-radio-group"
                    checked={disableQtyPerDay}
                    onChange={() => setDisableQtyPerDay(true)}
                  />
                </Form.Field>
                <Form.Field
                  required={disableQtyPerDay}
                  disabled={!disableQtyPerDay}
                >
                  <input
                    type="number"
                    step="any"
                    min="0"
                    className="per-day-input"
                    name="per-day-radio-group"
                    required={disableQtyPerDay}
                    defaultValue={_.get(
                      event,
                      "extendedProps.adus_per_day",
                      null
                    )}
                    onChange={(e) => setAdusPerDay(e.target.value)}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid>
          </Modal.Content>
          <Modal.Actions>
            {isEditing && (
              <Button
                type="cancel"
                color="red"
                onClick={(e) => {
                  e.preventDefault();
                  setShowRemoveEventConfirm(true);
                }}
                floated="left"
              >
                <Icon name="trash" /> {t("calendar.events.remove")}
              </Button>
            )}
            <Button type="cancel" color="red" inverted onClick={onClose}>
              <Icon name="close" /> {t("cancel")}
            </Button>
            <Button color="green" inverted type="submit">
              <Icon name="checkmark" /> {t("save")}
            </Button>
          </Modal.Actions>
        </Modal>
      </Form>
    </>
  );
};

EventModal.propTypes = {
  event: PropTypes.object.isRequired,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
  onDelete: PropTypes.func,
  groups: PropTypes.arrayOf(PropTypes.object),
  genericGroups: PropTypes.arrayOf(PropTypes.object),
  showGroupWarning: PropTypes.func,
  warningShowed: PropTypes.bool,
  columns: PropTypes.object,
  items: PropTypes.array,
};

EventModal.defaultProps = {
  genericGroups: [],
  onSubmit: _.noop,
  onClose: _.noop,
  onDelete: _.noop,
  columns: {},
  items: []
};

const mapStateToProps = (state) => ({
  columns: state.dataset.columns,
  items: state.items.items,
});

export default connect(mapStateToProps)(EventModal);
